"use client";

import type { AnchorHTMLAttributes } from "react";
import {
  FC,
  forwardRef,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { usePathname } from "apps/website/i18n/navigation";
import clsx from "clsx";
import ListItem from "apps/website/components/base/ListItem/ListItem";
import { IHeaderLink } from "apps/website/components/base/Button/Button.map";
import { isLocalPath } from "apps/website/utils/content/links";

import Icon from "../../base/Icon/Icon";
import Image from "../../base/Image/Image";
import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";
import { LinkWithQuery } from "../../overrides/LinkWithQuery/LinkWithQuery";

export interface INavigationItem {
  item: IHeaderLink,
  className?: string;
  tabIndex?: number;
  children?: ReactNode;
  onClick?: () => void;
  handleShowChildren?: (shouldShow: boolean) => void;
  hasChildren?: boolean;
  disableToggle?: boolean;
}

export interface IAnchorTag extends INavigationItem {
  isActiveParentPath?: boolean;
}

type AnchorTagOwnProps = {
  item: IHeaderLink;
  tabIndex?: number;
  /** e.g. close mobile menu — not the same prop as `Link`’s merged `onClick`. */
  onMenuClose?: () => void;
  isActiveParentPath?: boolean;
};

type AnchorTagProps = AnchorTagOwnProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof AnchorTagOwnProps>;

const AnchorTag = forwardRef<HTMLAnchorElement, AnchorTagProps>(({
  item,
  tabIndex,
  onMenuClose,
  isActiveParentPath,
  onClick: linkOnClick,
  className,
  ...rest
}, ref) => (
  <a
    { ...rest }
    ref={ref}
    tabIndex={tabIndex}
    href={item.to}
    className={clsx(className, { relative: item.type !== "icon" })}
    onClick={(event) => {
      item.onClick?.(event);
      linkOnClick?.(event);
      onMenuClose?.();
    }}
  >
    { item.type === "icon" && (
      <div className="xl:hidden flex items-center justify-center mr-1">
        <Icon icon={item.icon?.icon ?? "gift"} size={item.icon?.size ?? "small"} color={item.icon?.color}/>
      </div>
    ) }

    <TextSubtitle
      tag="div"
      align={{ xl: "center" }}
      className={clsx(
        "text-2xl md:text-lg xl:text-base uppercase",
        { "xl:not-sr-only sr-only": item.type === "icon" },
      )}
    >
      { item.name }
    </TextSubtitle>
    <div className={`hidden xl:block absolute -bottom-0.5 left-0 h-0.5 group-hover:w-full transition-all duration-300 bg-black ${isActiveParentPath ? "w-full" : "w-0"}`}></div>
  </a>
));

AnchorTag.displayName = "AnchorTag";

const NavigationLink: FC<IAnchorTag> = (({ item, onClick, isActiveParentPath, tabIndex }) => {
  if (isLocalPath(item.to)) {
    return (
      <LinkWithQuery href={ item.to } legacyBehavior>
        <AnchorTag item={item} onMenuClose={onClick} isActiveParentPath={isActiveParentPath} tabIndex={tabIndex}/>
      </LinkWithQuery>
    );
  }

  return (
    <AnchorTag item={item} onMenuClose={onClick} isActiveParentPath={isActiveParentPath} tabIndex={tabIndex} />
  );
});

const CollapsableNavItem: FC<{
  tabIndex?: number;
  handleShowChildrenToggle: () => void;
  showChildren: boolean;
  item: IHeaderLink;
  isActiveParentPath?: boolean;
}> = ({ tabIndex, handleShowChildrenToggle, showChildren, item, isActiveParentPath }) => (

  <button tabIndex={ tabIndex } className="relative flex justify-between items-center w-full" onClick={ handleShowChildrenToggle }>
    <TextSubtitle tag="div" size={3} className={clsx("uppercase -bottom-0.5", {
      "text-base md:text-sm xl:text-base": item.type === "authMenu",
      "text-2xl md:text-lg xl:text-base": item.type !== "authMenu",
    })

    } align={ { xl: "center" } }>{ item.name }</TextSubtitle>

    <div className={ `transform transition-transform duration-300 xl:hidden ${showChildren ? "rotate-180" : "rotate-0"}` }>
      <Icon
        icon="arrowDown"
        size="xsmall"
      />
    </div>
    <div className={ `hidden xl:block absolute -bottom-0.5 left-0 h-0.5 group-hover:w-full transition-all duration-300 bg-black ${isActiveParentPath ? "w-full" : "w-0"}` }></div>
  </button>
);

const NavigationItem: FC<INavigationItem> = ({
  item,
  className,
  tabIndex,
  onClick,
  hasChildren,
  handleShowChildren,
  children,
  disableToggle = false,
}) => {

  const path = usePathname();
  const isActiveParentPath = `/${path.split("/")[1]}` === item.to;

  const [ showChildren, setShowChildren ] = useState(false);

  const handleShowChildrenToggle = () => setShowChildren(!showChildren);

  useEffect(() => {
    if (!handleShowChildren) return;
    handleShowChildren(showChildren);
  }, [ showChildren ]);

  return (
    <ListItem
      key={ item.name }
      component="NavigationItem"
      className={clsx(`xl:py-2 xl:px-4 xl:first:pl-0 xl:pl-0 xl:p-4 group ${className}`, {
        "lg:pb-1 px-0": item.type === "icon",
        "px-4": item.type !== "icon",
      })}
      align="center"
    >
      <div className={clsx(" w-full ", {
        "pb-0 lg:pb-1 xl:pb-0 ": item.type === "icon",
        "pb-4 xl:pb-0": item.type !== "icon",
      })}>
        <div className={
          clsx("xl:relative flex items-center justify-between", {
            "pb-4 xl:pb-0 border-b border-solid border-grey-400 xl:border-b-0": item.type !== "icon",
          })}>
          { item.type === "icon" && (
            <div className="w-11 mr-1 xl:flex xl:items-center xl:justify-center hidden">
              <Icon icon={item.icon?.icon ?? "gift"} size={item.icon?.size ?? "small"} color={item.icon?.color} />
            </div>) }

          { item.type === "authMenu" && (
            <div className="w-11 mr-1">
              <Image image={ item.image ?? { src: "/images/account/account-avatar-fallback.svg", height: 100, width: 100 } } alt="User avatar" />
            </div>
          ) }
          { hasChildren ? (
            <>
              { disableToggle ? (
                <div className="w-full">
                  <TextSubtitle tag="div" size={3} className={
                    clsx("uppercase -bottom-0.5", {
                      "text-base md:text-sm xl:text-base": item.type === "authMenu",
                      "text-2xl md:text-lg xl:text-base": item.type !== "authMenu",
                    })
                  } align={ { xl: "center" } }>{ item.name }</TextSubtitle>
                </div>
              ) :
                (
                  <CollapsableNavItem
                    tabIndex={tabIndex}
                    handleShowChildrenToggle={handleShowChildrenToggle}
                    showChildren={showChildren}
                    item={item}
                    isActiveParentPath={isActiveParentPath}
                  />
                ) }
              <div className="hidden xl:block xl:transition-all xl:ease-in-out xl:duration-300 xl:absolute -bottom-4 xl:-bottom-7 xl:left-1/2 xl:-translate-x-1/2 xl:max-h-0 overflow-hidden xl:group-hover:max-h-4 xl:group-focus-within:max-h-4">
                <div className={ `xl:border-transparent xl:border-8 ${item.type === "authMenu" ? "xl:border-b-black" : "xl:border-b-light-grey"}` }></div>
              </div>
            </>
          ) : (
            <NavigationLink item={item} onClick={onClick} isActiveParentPath={isActiveParentPath} tabIndex={tabIndex} />
          ) }
        </div>
        { children }
      </div>
    </ListItem>
  );
};

export default NavigationItem;
